<template>
    <div class="cash-page">
        <header class="top-nav">
            <button type="button" class="back-btn" @click="$router.go(-1)">
                <van-icon name="arrow-left" />
            </button>
            <div class="nav-title">{{ $t('cash.withdraw') }}</div>
        </header>

        <main class="page-content">
            <section class="balance-card">
                <div class="balance-copy">
                    <p>{{ $t('user.availableBalance') }}</p>
                    <h2>{{ formatCurrency(data.money) }}</h2>
                </div>
                <div class="balance-visual">
                    <div class="wallet-halo"></div>
                    <div class="wallet-body">
                        <van-icon name="balance-o" />
                    </div>
                </div>
            </section>

            <section class="section-block">
                <label class="section-label">{{ getLabel('pageCash.networkLabel', '提现网络') }}</label>
                <div class="dropdown-wrap">
                    <button type="button" class="network-trigger" @click="dropdownOpen = !dropdownOpen">
                        <div class="trigger-left">
                            <img
                                v-if="selectedWallet.logo"
                                :src="selectedWallet.logo"
                                alt=""
                                class="coin-icon"
                            >
                            <span :class="{ placeholder: !selectedWallet.name }">
                                {{ selectedWalletLabel }}
                            </span>
                        </div>
                        <van-icon :name="dropdownOpen ? 'arrow-up' : 'arrow-down'" />
                    </button>

                    <div v-if="dropdownOpen" class="network-list">
                        <button
                            v-for="item in walletList"
                            :key="item.value"
                            type="button"
                            class="network-item"
                            @click="selectWallet(item)"
                        >
                            <img v-if="item.logo" :src="item.logo" alt="" class="coin-icon">
                            <div class="network-meta">
                                <strong>{{ item.bank }}</strong>
                                <small>{{ getWalletChain(item) }}</small>
                            </div>
                        </button>
                        <button type="button" class="network-item add-wallet" @click="goAddWallet">
                            <div class="add-icon">
                                <van-icon name="plus" />
                            </div>
                            <div class="network-meta">
                                <strong>{{ $t('account.add') }}</strong>
                                <small>{{ getLabel('pageCash.addWalletHint', '前往绑定提现账户') }}</small>
                            </div>
                        </button>
                    </div>
                </div>
            </section>

            <section class="section-block">
                <label class="section-label">{{ $t('account.address') }}</label>
                <div class="address-input-wrap">
                    <input
                        v-model.trim="walletAddress"
                        type="text"
                        class="address-input"
                        :placeholder="getLabel('pageCash.addressPlaceholder', '输入或粘贴钱包地址')"
                    >
                    <button type="button" class="paste-btn" @click="pasteAddress">{{ getLabel('pageCash.paste', '粘贴') }}</button>
                </div>
            </section>

            <section class="section-block">
                <div class="section-head">
                    <label class="section-label no-margin">{{ $t('cash.amount') }}</label>
                    <span class="fee-text">{{ $t('cash.charge') }}: {{ formatCurrency(displayFee) }}</span>
                </div>
                <div class="amount-card">
                    <div class="amount-input-wrap">
                        <input
                            v-model.trim="form.money"
                            type="number"
                            min="0"
                            step="0.01"
                            class="amount-input"
                            :placeholder="minWithdrawPlaceholder"
                            @input="changeAmount"
                        >
                        <div class="amount-actions">
                            <span class="divider"></span>
                            <button type="button" class="all-btn" @click="subAll">{{ getLabel('pageCash.all', '所有') }}</button>
                        </div>
                    </div>
                    <p class="amount-tip">{{ getLabel('pageCash.minWithdraw', '最小提现金额') }} {{ formatCurrency(minWithdrawAmount) }} USD</p>
                    <p class="amount-tip">{{ getLabel('pageCash.actualArrival', '实际到账') }}: {{ actualAmountDisplay }}</p>
                </div>
            </section>

            <section class="section-block">
                <label class="section-label">{{ getLabel('pageCash.payPassword', '支付密码') }}</label>
                <div class="password-wrap">
                    <input
                        v-model.trim="form.password"
                        :type="passwordVisible ? 'text' : 'password'"
                        class="password-input"
                        :placeholder="$t('cash.tips')"
                    >
                    <button type="button" class="eye-btn" @click="passwordVisible = !passwordVisible">
                        <van-icon :name="passwordVisible ? 'eye-o' : 'closed-eye'" />
                    </button>
                </div>
            </section>

            <button type="button" class="submit-btn" :disabled="submitLoading" @click="submit">
                {{ submitLoading ? $t('utils.loading') : $t('cash.withdraw') }}
                <van-icon name="share" />
            </button>

            <section class="notice-card">
                <van-icon name="info-o" />
                <div>
                    <strong>{{ getLabel('pageCash.noticeTitle', '重要提示') }}</strong>
                    <p>{{ getLabel('pageCash.noticeText', '为保障您的资产安全，请仔细核对提现网络及钱包地址；首次提现可联系在线客服完成钱包地址安全备案，后续如检测到提现地址变更，平台将由在线客服与您联系并进行二次安全核验；提现申请提交后将进入链上处理流程，每笔提现手续费以当前通道为准，预计 0-24 小时到账。') }}</p>
                </div>
            </section>

            <section class="history-section">
                <div class="history-title">{{ getLabel('pageCash.historyTitle', '最近提现记录') }}</div>
                <div class="history-table">
                    <div class="table-head">
                        <span>{{ getLabel('pageCash.tableNetwork', '网络') }}</span>
                        <span>{{ getLabel('pageCash.tableAmount', '金额') }}</span>
                        <span>{{ getLabel('pageCash.tableFee', '手续费') }}</span>
                        <span>{{ getLabel('pageCash.tableArrival', '实际到账') }}</span>
                        <span>{{ getLabel('pageCash.tableStatus', '状态') }}</span>
                        <span>{{ getLabel('pageCash.tableTime', '时间') }}</span>
                    </div>

                    <div v-if="recordList.length" class="table-body">
                        <div v-for="item in recordList" :key="item.id || item.time" class="table-row">
                            <span class="col-network">{{ item.bank || item[lang] || $t('cash.withdraw') }}</span>
                            <span class="col-money">{{ formatCurrency(item.money) }}</span>
                            <span class="col-charge">{{ formatCurrency(item.charge) }}</span>
                            <span class="col-arrival">{{ formatCurrency(item.money2 || item.money) }}</span>
                            <span class="col-status" :class="statusClass(item.status)">{{ statusText(item.status) }}</span>
                            <span class="col-time">{{ item.time }}</span>
                        </div>
                    </div>

                    <div v-else class="table-empty">{{ getLabel('pageCash.emptyRecords', '暂无提现记录') }}</div>
                </div>
            </section>
        </main>

        <div v-if="showSuccessPopup" class="success-popup-mask" @click.self="closeSuccessPopup">
            <div class="success-popup">
                <div class="success-icon">
                    <van-icon name="passed" />
                </div>
                <h3>{{ getLabel('pageCash.successTitle', '提交成功') }}</h3>
                <p>{{ getLabel('pageCash.successText', '提现申请已提交，请留意下方提现记录和到账状态。') }}</p>
                <button type="button" class="success-btn" @click="closeSuccessPopup">{{ getLabel('pageCash.gotIt', '我知道了') }}</button>
            </div>
        </div>

        <div v-if="setPwd" class="pwd-mask">
            <div class="pwd-dialog">
                <div class="pwd-icon">
                    <van-icon name="shield-o" />
                </div>
                <h3>{{ $t('cash.setPwdFirst') }}</h3>
                <p>{{ getLabel('pageCash.setPwdHint', '提现前需要先设置支付密码，点击下方立即前往。') }}</p>
                <button type="button" class="pwd-btn" @click="$router.push('/setpaypwd')">{{ $t('cash.setNow') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import Fetch from "../../utils/fetch";
import { Icon, Toast } from "vant";

Vue.use(Icon);
Vue.use(Toast);

export default {
    name: "cash",
    data() {
        return {
            lang: this.$i18n.locale || "zh_cn",
            data: {},
            walletList: [],
            selectedWallet: {},
            dropdownOpen: false,
            passwordVisible: false,
            submitLoading: false,
            showSuccessPopup: false,
            setPwd: false,
            walletAddress: "",
            form: {
                money: "",
                password: "",
                bank_id: 0,
            },
            charge: 0,
            type: 1,
            rate: 1,
            mark: "$",
            chargeTip: "",
            actualAmount: "",
            minWithdrawAmount: 100,
            recordList: [],
            appOriginalStyles: null,
            bodyOriginalBackground: "",
        };
    },
    computed: {
        selectedWalletLabel() {
            return this.selectedWallet.name || this.getLabel("pageCash.selectNetwork", "请选择提现网络");
        },
        displayFee() {
            const money = Number(this.form.money || 0);
            if (!this.charge) {
                return 0;
            }
            if (this.type === 1) {
                return Number(this.rate || 1) * Number(this.charge || 0);
            }
            return money > 0 ? money * Number(this.charge || 0) / 100 : 0;
        },
        actualAmountDisplay() {
            const money = Number(this.form.money || 0);
            if (!money) {
                return "$0.00";
            }
            if (this.type === 1) {
                return this.formatCurrency(Math.max(money - Number(this.charge || 0), 0));
            }
            if (this.rate) {
                return (this.mark || "") + (money / this.rate).toFixed(0);
            }
            return this.formatCurrency(money);
        },
        minWithdrawPlaceholder() {
            return this.minWithdrawAmount ? this.minWithdrawAmount.toFixed(2) : "100.00";
        },
    },
    created() {
        this.$parent.footer("user", false);
    },
    mounted() {
        this.applyFullScreenLayout();
        this.start();
        document.addEventListener("click", this.handleDocumentClick);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleDocumentClick);
        this.restoreFullScreenLayout();
    },
    methods: {
        getLabel(key, defaultText) {
            return this.$te(key) ? this.$t(key) : defaultText;
        },
        applyFullScreenLayout() {
            const app = document.getElementById("app");
            if (!app) {
                return;
            }
            this.appOriginalStyles = {
                maxWidth: app.style.maxWidth,
                width: app.style.width,
                margin: app.style.margin,
                backgroundColor: app.style.backgroundColor,
                paddingBottom: app.style.paddingBottom,
            };
            this.bodyOriginalBackground = document.body.style.background;
            app.style.maxWidth = "100%";
            app.style.width = "100%";
            app.style.margin = "0";
            app.style.backgroundColor = "#000000";
            app.style.paddingBottom = "0";
            document.body.style.background = "#000000";
        },
        restoreFullScreenLayout() {
            const app = document.getElementById("app");
            if (app && this.appOriginalStyles) {
                app.style.maxWidth = this.appOriginalStyles.maxWidth || "";
                app.style.width = this.appOriginalStyles.width || "";
                app.style.margin = this.appOriginalStyles.margin || "";
                app.style.backgroundColor = this.appOriginalStyles.backgroundColor || "";
                app.style.paddingBottom = this.appOriginalStyles.paddingBottom || "";
            }
            document.body.style.background = this.bodyOriginalBackground || "";
        },
        async start() {
            const walletRes = await Fetch("/user/my_bank");
            this.data = walletRes.data || {};
            this.setPwd = !!walletRes.data.intPwd;
            this.minWithdrawAmount = Number(
                walletRes.data.min_withdraw ||
                walletRes.data.min_cash ||
                walletRes.data.minmoney ||
                100
            );

            const bankList = Array.isArray(walletRes.data.bank) ? walletRes.data.bank : [];
            this.walletList = bankList.map((item) => ({
                ...item,
                value: item.id,
                name: item.bank,
                bank: item.bank,
                logo: item.logo || "",
                chain: item.mark,
            }));

            if (this.walletList.length) {
                this.selectWallet(this.walletList[0]);
            }

            const recordRes = await Fetch("/user/funds", { reason_id: 2 });
            const list = Array.isArray(recordRes.data.list) ? recordRes.data.list : [];
            this.recordList = list.map((item) => ({
                ...item,
                charge: item.charge || 0,
                money2: item.money2 || item.money,
                bank: item.bank || item.name || item[this.lang] || this.$t("cash.withdraw"),
            }));
        },
        selectWallet(item) {
            this.selectedWallet = item;
            this.dropdownOpen = false;
            this.form.bank_id = item.value;
            this.walletAddress = item.account || "";
            this.charge = Number(item.charge || 0);
            this.type = Number(item.type || 1);
            this.rate = Number(item.rate || 1);
            this.mark = item.mark || "$";
            this.changeAmount();
        },
        getWalletChain(item) {
            if (item.mark && /^[A-Z]{2,10}$/.test(String(item.mark))) {
                return item.mark;
            }
            const bank = String(item.bank || "");
            const matched = bank.match(/(TRC20|ERC20|BEP20|TRX|ETH|BSC|POLYGON|ARB|OP)/i);
            return matched ? matched[1].toUpperCase() : "NETWORK";
        },
        changeAmount() {
            const money = Number(this.form.money || 0);
            if (!money) {
                this.actualAmount = "";
                this.chargeTip = "";
                return;
            }
            if (this.type === 1) {
                this.actualAmount = "=" + Math.max(money - Number(this.charge || 0), 0).toFixed(2) + (this.mark || "");
            } else if (this.rate) {
                this.actualAmount = "≈" + (this.mark || "") + (money / this.rate).toFixed(0);
            }
        },
        subAll() {
            this.form.money = Number(this.data.money || 0).toFixed(2);
            this.changeAmount();
        },
        async pasteAddress() {
            if (navigator.clipboard && window.isSecureContext) {
                try {
                    const text = await navigator.clipboard.readText();
                    if (text) {
                        this.walletAddress = text;
                        Toast(this.getLabel("pageCash.pasteSuccess", "粘贴成功"));
                        return;
                    }
                } catch (error) {
                    // fallback below
                }
            }
            Toast(this.getLabel("pageCash.pasteManual", "请手动粘贴钱包地址"));
        },
        async submit() {
            if (!this.form.money) {
                Toast(this.$t("cash.withdrawalAmountEmpty"));
                return;
            }
            if (Number(this.form.money) < this.minWithdrawAmount) {
                Toast(this.getLabel("pageCash.minWithdrawError", "最小提现金额为") + " " + this.formatCurrency(this.minWithdrawAmount));
                return;
            }
            if (Number(this.form.money) > Number(this.data.money || 0)) {
                Toast(this.$t("cash.withdrawalAmountHigh"));
                return;
            }
            if (!this.form.bank_id) {
                Toast(this.getLabel("pageCash.selectNetwork", "请选择提现网络"));
                return;
            }
            if (this.walletAddress !== (this.selectedWallet.account || "")) {
                Toast(this.getLabel("pageCash.bindWalletOnly", "当前接口仅支持已绑定钱包地址，请先新增钱包后再提现"));
                return;
            }
            if (!this.form.password) {
                Toast(this.$t("cash.tips"));
                return;
            }
            this.submitLoading = true;
            try {
                await Fetch("/user/cost_apply", {
                    money: this.form.money,
                    bank_id: this.form.bank_id,
                    passwd: this.form.password,
                });
                this.showSuccessPopup = true;
                this.form.password = "";
                await this.start();
            } finally {
                this.submitLoading = false;
            }
        },
        closeSuccessPopup() {
            this.showSuccessPopup = false;
        },
        goAddWallet() {
            this.$router.push("/addcountUsdt");
        },
        statusText(status) {
            const value = String(status);
            if (value === "1") {
                return this.$t("utils.complete");
            }
            if (value === "2") {
                return this.$t("invest.failed");
            }
            return this.getLabel("pageCash.processing", "处理中");
        },
        statusClass(status) {
            const value = String(status);
            if (value === "1") {
                return "success";
            }
            if (value === "2") {
                return "failed";
            }
            return "pending";
        },
        formatCurrency(value) {
            return "INR" + Number(value || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        handleDocumentClick(event) {
            if (!this.$el || !this.dropdownOpen) {
                return;
            }
            const dropdown = this.$el.querySelector(".dropdown-wrap");
            if (dropdown && !dropdown.contains(event.target)) {
                this.dropdownOpen = false;
            }
        },
    },
};
</script>

<style lang="less" scoped>
.cash-page {
    min-height: 100vh;
    background: #000;
    color: #fff;
}

.top-nav {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    padding: 0 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(12px);
}

.back-btn {
    position: absolute;
    left: 16px;
    top: 50%;
    width: 28px;
    height: 28px;
    margin-top: -14px;
    border: 0;
    background: transparent;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
}

.back-btn .van-icon {
    font-size: 22px;
}

.nav-title {
    font-size: 18px;
    font-weight: 700;
}

.page-content {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 22px 14px 32px;
    box-sizing: border-box;
}

.balance-card {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    min-height: 128px;
    margin-bottom: 26px;
    padding: 22px 20px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 18px;
    background: linear-gradient(135deg, #131313 0%, #090909 100%);
}

.balance-copy {
    position: relative;
    z-index: 2;
}

.balance-copy p {
    margin: 0 0 8px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
}

.balance-copy h2 {
    margin: 0;
    color: #fff;
    font-size: 22px;
    font-weight: 800;
}

.balance-visual {
    position: relative;
    z-index: 1;
    width: 124px;
    height: 92px;
    flex-shrink: 0;
}

.wallet-halo {
    position: absolute;
    top: 12px;
    right: 0;
    width: 92px;
    height: 92px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(45, 91, 255, 0.4) 0%, rgba(45, 91, 255, 0) 70%);
    filter: blur(8px);
}

.wallet-body {
    position: absolute;
    top: 4px;
    right: 16px;
    width: 74px;
    height: 74px;
    border: 1px solid rgba(45, 91, 255, 0.28);
    border-radius: 18px;
    background: linear-gradient(180deg, rgba(24, 27, 55, 0.95) 0%, rgba(11, 13, 29, 0.95) 100%);
    box-shadow: 0 0 26px rgba(45, 91, 255, 0.35);
    color: #2d5bff;
    display: flex;
    align-items: center;
    justify-content: center;
}

.wallet-body .van-icon {
    font-size: 34px;
}

.section-block {
    margin-bottom: 22px;
}

.section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.section-label {
    display: block;
    margin: 0 0 12px 2px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
}

.section-label.no-margin {
    margin: 0;
}

.fee-text {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
}

.dropdown-wrap {
    position: relative;
}

.network-trigger,
.address-input-wrap,
.password-wrap,
.amount-card,
.history-table,
.notice-card {
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(12px);
}

.network-trigger {
    width: 100%;
    height: 52px;
    padding: 0 16px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.trigger-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.trigger-left span {
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 15px;
}

.trigger-left .placeholder {
    color: rgba(255, 255, 255, 0.35);
}

.coin-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.network-list {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 8px);
    z-index: 40;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 16px;
    background: rgba(87, 87, 87, 0.62);
    backdrop-filter: blur(18px);
}

.network-item {
    width: 100%;
    padding: 14px 16px;
    border: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: transparent;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 12px;
    text-align: left;
}

.network-item:last-child {
    border-bottom: 0;
}

.network-meta strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
}

.network-meta small {
    display: inline-block;
    margin-top: 3px;
    color: rgba(255, 255, 255, 0.42);
    font-size: 11px;
}

.add-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(45, 91, 255, 0.14);
    color: #2d5bff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.address-input-wrap,
.password-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 14px;
    height: 54px;
}

.address-input,
.password-input,
.amount-input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: #fff;
    font-size: 15px;
}

.address-input::placeholder,
.password-input::placeholder,
.amount-input::placeholder {
    color: rgba(255, 255, 255, 0.28);
}

.paste-btn,
.eye-btn {
    border: 0;
    background: transparent;
}

.paste-btn {
    min-width: 48px;
    height: 34px;
    padding: 0 12px;
    border-radius: 10px;
    background: rgba(45, 91, 255, 0.12);
    color: #2d5bff;
    font-size: 14px;
    font-weight: 700;
}

.eye-btn {
    color: rgba(255, 255, 255, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
}

.eye-btn .van-icon {
    font-size: 20px;
}

.amount-card {
    padding: 14px 14px 12px;
}

.amount-input-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 50px;
    padding: 0 10px 0 12px;
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.02);
}

.amount-input {
    font-size: 28px;
    font-weight: 700;
}

.amount-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.divider {
    width: 1px;
    height: 18px;
    background: rgba(255, 255, 255, 0.08);
}

.all-btn {
    border: 0;
    background: transparent;
    color: #2d5bff;
    font-size: 14px;
    font-weight: 700;
}

.amount-tip {
    margin: 10px 2px 0;
    color: rgba(255, 255, 255, 0.48);
    font-size: 12px;
}

.submit-btn,
.success-btn,
.pwd-btn {
    width: 100%;
    height: 52px;
    border: 0;
    border-radius: 14px;
    background: linear-gradient(135deg, #2d5bff 0%, #104af0 100%);
    box-shadow: 0 4px 15px rgba(45, 91, 255, 0.3);
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.submit-btn[disabled] {
    opacity: 0.65;
}

.notice-card {
    margin-top: 22px;
    padding: 16px 14px;
    border-left: 4px solid #2d5bff;
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.notice-card .van-icon {
    margin-top: 2px;
    color: #2d5bff;
    font-size: 20px;
    flex-shrink: 0;
}

.notice-card strong {
    display: block;
    margin-bottom: 6px;
    font-size: 14px;
}

.notice-card p {
    margin: 0;
    color: rgba(255, 255, 255, 0.66);
    font-size: 12px;
    line-height: 1.75;
}

.history-section {
    margin-top: 26px;
}

.history-title {
    margin: 0 0 14px 2px;
    font-size: 18px;
    font-weight: 700;
}

.history-table {
    overflow: hidden;
}

.table-head,
.table-row {
    display: grid;
    grid-template-columns: 82px 78px 78px 86px 64px minmax(92px, 1fr);
    gap: 8px;
    align-items: start;
}

.table-head {
    padding: 14px 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    background: rgba(255, 255, 255, 0.02);
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
    font-weight: 700;
}

.table-body {
    padding: 8px 0;
}

.table-row {
    padding: 12px 12px;
    color: #fff;
    font-size: 12px;
}

.col-network {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.col-money,
.col-charge,
.col-arrival,
.col-time {
    text-align: right;
}

.col-status {
    justify-self: center;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 11px;
    white-space: nowrap;
}

.col-status.success {
    background: rgba(34, 197, 94, 0.16);
    color: #3ddc84;
}

.col-status.pending {
    background: rgba(245, 158, 11, 0.16);
    color: #fbbf24;
}

.col-status.failed {
    background: rgba(239, 68, 68, 0.16);
    color: #fb7185;
}

.table-empty {
    padding: 36px 16px;
    color: rgba(255, 255, 255, 0.36);
    font-size: 14px;
    text-align: center;
}

.success-popup-mask,
.pwd-mask {
    position: fixed;
    inset: 0;
    z-index: 120;
    background: rgba(0, 0, 0, 0.68);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.success-popup,
.pwd-dialog {
    width: 100%;
    max-width: 320px;
    padding: 28px 22px 22px;
    border: 1px solid rgba(45, 91, 255, 0.2);
    border-radius: 18px;
    background: linear-gradient(180deg, #081122 0%, #050a16 100%);
    box-shadow: 0 18px 45px rgba(0, 0, 0, 0.38);
    text-align: center;
}

.success-icon,
.pwd-icon {
    width: 66px;
    height: 66px;
    margin: 0 auto 16px;
    border-radius: 50%;
    background: rgba(45, 91, 255, 0.14);
    color: #2d5bff;
    display: flex;
    align-items: center;
    justify-content: center;
}

.success-icon .van-icon,
.pwd-icon .van-icon {
    font-size: 34px;
}

.success-popup h3,
.pwd-dialog h3 {
    margin: 0 0 10px;
    color: #fff;
    font-size: 22px;
    font-weight: 700;
}

.success-popup p,
.pwd-dialog p {
    margin: 0 0 20px;
    color: rgba(255, 255, 255, 0.64);
    font-size: 13px;
    line-height: 1.7;
}
</style>
